import { supabase } from './supabase';
import { ErrorDeApi } from './error-api';

export type TipoCuenta = 'efectivo' | 'billetera' | 'banco' | 'datafono' | 'otro';

export interface Cuenta {
  id: string;
  nombre: string;
  tipo: TipoCuenta;
  medioPagoDian: string;
  esEfectivo: boolean;
  activa: boolean;
  orden: number;
}

/** Código DIAN que se propone al crear la cuenta; el dueño puede elegir otro del catálogo. */
export const MEDIO_DIAN_SUGERIDO: Record<TipoCuenta, string> = {
  efectivo: '10',
  billetera: '47',
  banco: '45',
  datafono: '48',
  otro: 'ZZZ',
};

export interface MedioPagoDian {
  codigo: string;
  nombre: string;
}

/** Catálogo oficial que administra la plataforma, no una lista repetida en el cliente. */
export async function listarMediosPagoDian(): Promise<MedioPagoDian[]> {
  const { data, error, status } = await supabase.from('dian_medios_pago').select('pago_codigo, pago_nombre').order('pago_codigo');
  if (error) throw new ErrorDeApi(error, status);
  return (data ?? []).map((m) => ({ codigo: m.pago_codigo, nombre: m.pago_nombre }));
}

export const TIPOS_CUENTA: TipoCuenta[] = ['efectivo', 'billetera', 'banco', 'datafono', 'otro'];

export const ETIQUETA_TIPO_CUENTA: Record<TipoCuenta, string> = {
  efectivo: 'Efectivo en caja',
  billetera: 'Billetera digital (Nequi, Daviplata…)',
  banco: 'Cuenta bancaria o transferencia (Bre-B, PSE…)',
  datafono: 'Datáfono',
  otro: 'Otro',
};

export async function listarCuentas(negocioId: string, soloActivas = true): Promise<Cuenta[]> {
  let q = supabase
    .from('cuentas_negocio')
    .select('id, cuenta_nombre, cuenta_tipo, medio_pago_dian, es_efectivo, cuenta_activa, cuenta_orden')
    .eq('negocio_id', negocioId)
    .is('eliminado_en', null)
    .order('cuenta_orden')
    .order('cuenta_nombre');
  if (soloActivas) q = q.eq('cuenta_activa', true);
  const { data, error, status } = await q;
  if (error) throw new ErrorDeApi(error, status);
  return (data ?? []).map((c) => ({
    id: c.id,
    nombre: c.cuenta_nombre,
    tipo: c.cuenta_tipo as TipoCuenta,
    medioPagoDian: c.medio_pago_dian,
    esEfectivo: c.es_efectivo,
    activa: c.cuenta_activa,
    orden: Number(c.cuenta_orden),
  }));
}

export async function crearCuenta(
  negocioId: string,
  nombre: string,
  tipo: TipoCuenta,
  medioPagoDian?: string
): Promise<void> {
  const { error } = await supabase.from('cuentas_negocio').insert({
    negocio_id: negocioId,
    cuenta_nombre: nombre.trim(),
    cuenta_tipo: tipo,
    medio_pago_dian: medioPagoDian || MEDIO_DIAN_SUGERIDO[tipo],
    es_efectivo: tipo === 'efectivo',
  });
  if (error) throw new Error(error.code === '23505' ? 'Ya tienes una cuenta con ese nombre.' : error.message);
}

export async function cambiarEstadoCuenta(negocioId: string, id: string, activa: boolean): Promise<void> {
  const { error, status } = await supabase
    .from('cuentas_negocio')
    .update({ cuenta_activa: activa })
    .eq('negocio_id', negocioId)
    .eq('id', id);
  if (error) throw new ErrorDeApi(error, status);
}

/** Los dos tributos que admite la base. Un producto sin impuestos asociados es exento: «ninguno»
 *  no es un tipo válido: un producto puede llevar varios. */
export type TipoImpuesto = 'iva' | 'inc';

export interface Impuesto {
  id: string;
  nombre: string;
  tipo: TipoImpuesto;
  tarifa: number;
  /** Si se cobra sobre el precio con descuento (`true`) o sobre el precio sin descuento. */
  reduceBase: boolean;
  activo: boolean;
}

/** Traduce los rechazos de la base; sin esto el CHECK de tipo o de tarifa llega en crudo. */
function mensajeImpuesto(codigo: string | undefined, crudo: string): string {
  if (codigo === '23505') return 'Ya tienes un impuesto con ese nombre.';
  if (codigo === '23514') return 'El impuesto debe ser IVA o INC, con una tarifa entre 0 y 60.';
  return crudo;
}

export const TIPOS_IMPUESTO: TipoImpuesto[] = ['iva', 'inc'];

export const ETIQUETA_TIPO_IMPUESTO: Record<TipoImpuesto, string> = {
  iva: 'Impuesto al valor agregado (IVA)',
  inc: 'Impuesto al consumo (INC)',
};

export async function listarImpuestos(negocioId: string, soloActivos = true): Promise<Impuesto[]> {
  let q = supabase
    .from('impuestos_negocio')
    .select('id, impuesto_nombre, impuesto_tipo, impuesto_tarifa, impuesto_descuento_reduce_base, impuesto_activo')
    .eq('negocio_id', negocioId)
    .is('eliminado_en', null)
    .order('impuesto_tarifa');
  if (soloActivos) q = q.eq('impuesto_activo', true);
  const { data, error, status } = await q;
  if (error) throw new ErrorDeApi(error, status);
  return (data ?? []).map((i) => ({
    id: i.id,
    nombre: i.impuesto_nombre,
    tipo: i.impuesto_tipo as TipoImpuesto,
    tarifa: Number(i.impuesto_tarifa),
    reduceBase: i.impuesto_descuento_reduce_base,
    activo: i.impuesto_activo,
  }));
}

export async function crearImpuesto(
  negocioId: string,
  nombre: string,
  tipo: TipoImpuesto,
  tarifa: number,
  reduceBase: boolean
): Promise<void> {
  const { error } = await supabase.from('impuestos_negocio').insert({
    negocio_id: negocioId,
    impuesto_nombre: nombre.trim(),
    impuesto_tipo: tipo,
    impuesto_tarifa: tarifa,
    impuesto_descuento_reduce_base: reduceBase,
  });
  if (error) throw new Error(mensajeImpuesto(error.code, error.message));
}

export async function cambiarEstadoImpuesto(negocioId: string, id: string, activo: boolean): Promise<void> {
  const { error, status } = await supabase
    .from('impuestos_negocio')
    .update({ impuesto_activo: activo })
    .eq('negocio_id', negocioId)
    .eq('id', id);
  if (error) throw new ErrorDeApi(error, status);
}

/** Una edición que la base rechaza por nombre repetido llega con su frase; el resto, traducido. */
function errorDeEdicion(error: { code?: string; message?: string }, status: number, repetido: string): Error {
  return error.code === '23505' ? new Error(repetido) : new ErrorDeApi(error, status);
}

/** Cambia nombre, tipo y medio DIAN; las ventas hechas conservan el nombre con el que se vendieron. */
export async function editarCuenta(
  negocioId: string,
  id: string,
  cambios: { nombre: string; tipo: TipoCuenta; medioPagoDian: string }
): Promise<void> {
  const { error, status } = await supabase
    .from('cuentas_negocio')
    .update({ cuenta_nombre: cambios.nombre.trim(), cuenta_tipo: cambios.tipo, medio_pago_dian: cambios.medioPagoDian })
    .eq('negocio_id', negocioId)
    .eq('id', id);
  if (error) throw errorDeEdicion(error, status, 'Ya tienes una cuenta con ese nombre.');
}

/** Borrado lógico: la cuenta sale del mostrador y del cuadre; la base lo frena si el cuadre aún la necesita. */
export async function eliminarCuenta(negocioId: string, id: string): Promise<void> {
  const { error, status } = await supabase
    .from('cuentas_negocio')
    .update({ eliminado_en: new Date().toISOString() })
    .eq('negocio_id', negocioId)
    .eq('id', id);
  if (error) throw new ErrorDeApi(error, status);
}

export async function editarImpuesto(
  negocioId: string,
  id: string,
  cambios: { nombre: string; tipo: TipoImpuesto; tarifa: number; reduceBase: boolean }
): Promise<void> {
  const { error, status } = await supabase
    .from('impuestos_negocio')
    .update({
      impuesto_nombre: cambios.nombre.trim(),
      impuesto_tipo: cambios.tipo,
      impuesto_tarifa: cambios.tarifa,
      impuesto_descuento_reduce_base: cambios.reduceBase,
    })
    .eq('negocio_id', negocioId)
    .eq('id', id);
  if (error) throw errorDeEdicion(error, status, 'Ya tienes un impuesto con ese nombre.');
}

/** Borrado lógico: los productos que lo tienen dejan de cobrarlo; lo ya vendido conserva su tarifa. */
export async function eliminarImpuesto(negocioId: string, id: string): Promise<void> {
  const { error, status } = await supabase
    .from('impuestos_negocio')
    .update({ eliminado_en: new Date().toISOString() })
    .eq('negocio_id', negocioId)
    .eq('id', id);
  if (error) throw new ErrorDeApi(error, status);
}
